import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function BuckinghamshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Buckinghamshire Pothole Claims | 150,000 Repairs in Five Years | Fixtyer"
        description="Buckinghamshire Council repaired almost 150,000 road defects in five years, yet B and C roads keep declining and 57% of the network is only surveyed every other year. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/buckinghamshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Buckinghamshire', url: '/councils/buckinghamshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Buckinghamshire spends more than double its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is road condition — and Buckinghamshire is AMBER overall because its B, C and unclassified roads are declining. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 1,823km — 57% of the Buckinghamshire network — and have historically been surveyed only once every two years. There is no network condition data at all for 2021/22 or 2023/24. At the last two comparable surveys, 24% and 23% of U-roads were in RED condition.',
          },
          {
            question: 'Does the drop to 6.5% RED U-roads in 2024/25 weaken my claim?',
            answer: 'No — it is not a comparable figure. Buckinghamshire switched to a new AEI survey methodology in 2024/25, and the council itself notes this "required a change in how condition is reported". The 6.5% measures structural maintenance required under a different standard, not the same RED category that previously sat at 23-24%.',
          },
          {
            question: 'Does the HS2 and East West Rail admission help my claim?',
            answer: 'Yes. Buckinghamshire publicly attributes the slowed progress on B, C and unclassified roads to heavy construction traffic from HS2 and East West Rail. That is an admission the council knows parts of its network are deteriorating faster than normal — which raises the bar for what a "reasonable" inspection regime looks like on affected routes.',
          },
          {
            question: 'Pothole repairs fell in 2024/25 — does that mean the roads are fixed?',
            answer: 'Buckinghamshire still repaired 27,770 defects in 2024/25 — about 76 every day — combining 14,817 pothole repairs with 12,953 spray-injection patches. The council also notes a single "repair" can be one patch covering multiple potholes, so the true defect count is higher.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Buckinghamshire Council" 
        councilSlug="buckinghamshire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/buckinghamshire"
        headline="Buckinghamshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Buckinghamshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Buckinghamshire: Record Spending, Declining Local Roads
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Buckinghamshire spends well over double its DfT capital allocation and earns GREEN scorecards 
            for spend and best practice. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — 
            because B and C roads in RED condition are up a third since 2020/21, and the council repaired 
            <strong className="text-rose-400"> almost 150,000 road defects in five years</strong> while 57% of 
            its network goes unsurveyed in alternate years.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">27,770</div>
            <div className="text-white font-medium mt-2">Defects repaired in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              14,817 pothole repairs plus 12,953 spray-injection patches — roughly 76 defects fixed 
              every single day across Buckinghamshire's 3,193km network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Buckinghamshire's own transparency report — 
            A-roads improving, everything else slipping
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (413km — 12.9% of network): improving</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3.8%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">down from 5.3% in 2020/21</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">28.1%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">68.2%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 66.3%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: main roads have genuinely improved. But A-roads are just one-eighth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (957km — 30% of network): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3.9%</td>
                    <td className="px-4 py-2 text-center">24.7%</td>
                    <td className="px-4 py-2 text-center">71.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">27.5%</td>
                    <td className="px-4 py-2 text-center">67.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">6.3%</td>
                    <td className="px-4 py-2 text-center">28.2%</td>
                    <td className="px-4 py-2 text-center">65.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">5.8%</td>
                    <td className="px-4 py-2 text-center">28.8%</td>
                    <td className="px-4 py-2 text-center">65.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.2%</td>
                    <td className="px-4 py-2 text-center font-bold">27.9%</td>
                    <td className="px-4 py-2 text-center font-bold">66.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads are up a third since 2020/21 (3.9% → 5.2%), and good-condition 
              roads have fallen from 71.4% to 66.9%. A third of the B/C network now needs — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£20.7m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£47.5m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">81.1%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Buckinghamshire spends well over <span className="text-emerald-400">double its DfT allocation</span> — 
              and local roads are still declining. The problem isn't the chequebook. The network is 
              deteriorating faster than even a GREEN-rated spend programme can repair it.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,823km Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            57% of the network is unclassified roads — surveyed only once every two years
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">24%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No data collected</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No data collected</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-medium">6.5%* <span className="text-surface-400 text-xs">(new methodology — not comparable)</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Alternate-Year Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Buckinghamshire's own report states the unclassified network "has historically been 
                surveyed once every 2 years." That means for incidents in 2021/22 or 2023/24, there is 
                <strong> no network-level condition data at all</strong> for the road type that makes up 
                57% of the county.
              </p>
              <p className="text-surface-600 text-sm">
                At the last two comparable surveys, roughly <strong>one in four U-roads was in RED condition</strong> — 
                approximately 420-440km of residential streets, estate roads and village routes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*The 6.5% Asterisk</h3>
              <p className="text-amber-800 text-sm mb-3">
                In 2024/25 the council switched to an AEI survey for unclassified roads, which 
                "required a change in how condition is reported." The 6.5% measures "structural 
                maintenance required" under the emerging PAS 2161 methodology.
              </p>
              <p className="text-amber-800 text-sm">
                A drop from 23% to 6.5% in two years isn't a miracle recovery — it's a different ruler. 
                The two figures cannot be compared, and the council says as much in its own footnotes.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for 
              knowing the condition of its roads. For Buckinghamshire's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it one of the blind years?</li>
              <li>• If 23-24% of U-roads were RED at the last comparable survey, what was done about yours?</li>
              <li>• How does the council track deterioration on a road it surveys every other year?</li>
              <li>• Can pre- and post-2024/25 condition records even be compared after the methodology change?</li>
            </ul>
            <p className="text-white font-medium">
              A council can't claim detailed knowledge of a network it only measures in alternate years 
              — and then changes how it measures.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            149,315 Defects in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many potholes this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Spray-injection patches</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total defects</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">17,191</td>
                  <td className="px-6 py-4 text-center">16,791</td>
                  <td className="px-6 py-4 text-center font-bold">33,982</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">15,891</td>
                  <td className="px-6 py-4 text-center">12,857</td>
                  <td className="px-6 py-4 text-center font-bold">28,748</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">17,061</td>
                  <td className="px-6 py-4 text-center">10,399</td>
                  <td className="px-6 py-4 text-center font-bold">27,460</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,851</td>
                  <td className="px-6 py-4 text-center">12,504</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">31,355</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">14,817</td>
                  <td className="px-6 py-4 text-center">12,953</td>
                  <td className="px-6 py-4 text-center font-bold">27,770</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">83,811</td>
                  <td className="px-6 py-4 text-center font-bold">65,504</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">149,315</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~82 Defects a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Buckinghamshire repairs around 82 road defects per day. 
                A network producing potholes at that rate is, by definition, a network where defects 
                routinely form between inspections — exactly the scenario where prior reports and 
                photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Counting Caveat</h3>
              <p className="text-amber-800 text-sm">
                The council's own footnote: "A pothole repair could be a patch covering multiple potholes, 
                however within these numbers will be counted as a single repair." The true number of 
                individual potholes treated is therefore <strong>higher</strong> than the published figures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The HS2 Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The HS2 and East West Rail Admission
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for declining local roads — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The five-year data table presented above indicates a general improvement in the condition of A roads, while progress on B, C, and unclassified roads has slowed. This is partly attributable to external pressures, including global inflation and the sustained impact of heavy construction traffic linked to HS2 and East West Rail (EWR) projects."
              source="Buckinghamshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Buckinghamshire formally acknowledges that heavy construction traffic is accelerating 
                deterioration on its B, C and unclassified roads. That's documented knowledge of an 
                elevated risk on specific corridors.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerated wear raises the standard for what a "reasonable" inspection 
                frequency looks like on affected routes under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road on or near an HS2/EWR construction traffic route?</li>
                <li>• Did the council increase inspection frequency on those corridors?</li>
                <li>• If deterioration was known to be accelerating, why wasn't the defect caught?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Run AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Buckinghamshire is not Derbyshire — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — invests well beyond its DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — ISO 55001 aligned, documented asset management strategy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition genuinely improving
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> ~81% of spend classed as preventative
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C roads in RED up a third since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 57% of the network surveyed only every other year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 23-24% of U-roads RED at the last comparable surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~150,000 defects repaired in five years — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted accelerated deterioration from HS2/EWR traffic
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and best-practice scorecards, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the alternate-year survey gap is your strongest structural argument</li>
              <li>• Location relative to HS2/EWR construction routes</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Buckinghamshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Buckinghamshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No HS2 corridor analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road decline documented</li>
                <li>✅ Alternate-year survey gap argued</li>
                <li>✅ 149,315 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Buckinghamshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=buckinghamshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Buckinghamshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Buckinghamshire spends more than double its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is 
                road condition — and Buckinghamshire is AMBER overall because its B, C and unclassified 
                roads are declining. Section 58 turns on whether the specific defect that damaged your 
                vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,823km — 57% of the Buckinghamshire network — and have historically been 
                surveyed only once every two years. There is no network condition data at all for 2021/22 
                or 2023/24. At the last two comparable surveys, 24% and 23% of U-roads were in RED condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the drop to 6.5% RED U-roads in 2024/25 weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it is not a comparable figure. Buckinghamshire switched to a new AEI survey methodology 
                in 2024/25, and the council itself notes this "required a change in how condition is reported." 
                The 6.5% measures "structural maintenance required" under a different standard, not the same 
                RED category that previously sat at 23-24%.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the HS2 and East West Rail admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Buckinghamshire publicly attributes the slowed progress on B, C and unclassified roads 
                to heavy construction traffic from HS2 and East West Rail. That is an admission the council 
                knows parts of its network are deteriorating faster than normal — which raises the bar for 
                what a "reasonable" inspection regime looks like on affected routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                Buckinghamshire still repaired 27,770 defects in 2024/25 — about 76 every day — combining 
                14,817 pothole repairs with 12,953 spray-injection patches. The council also notes a single 
                "repair" can be one patch covering multiple potholes, so the true defect count is higher.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.buckinghamshire.gov.uk/parking-roads-and-transport/transport-policies-strategies-and-plans/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Buckinghamshire Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
